import { c as _c } from "react/compiler-runtime";
import { AxiosInstance } from "axios";
import { useQuery, type UseQueryOptions } from "react-query";
import * as z from "zod";
import { useApi } from "@/hooks/useApi";
const repositorySchema = z.object({
  name: z.coerce.string()
});
export type Repository = z.infer<typeof repositorySchema>;
const searchResultSchema = z.object({
  data: z.array(repositorySchema),
  has_more: z.boolean()
});
export type SearchResult = z.infer<typeof searchResultSchema>;

/**
 * Low-level helper: hit a `{data, has_more}` endpoint and validate the
 * response. The endpoint is the caller's choice.
 *
 * Repository endpoints share the same pagination / search contract on the
 * backend (`UserRepositoryService::paginatedSearch`), so the only thing
 * that varies between callers is the URL + auth namespace.
 */
export async function searchRepositories({
  api,
  endpoint,
  search,
  perPage = 100,
  extraParams
}: {
  api: AxiosInstance;
  endpoint: string;
  search: string;
  perPage?: number;
  /** Extra query params to send with the repository search. */
  extraParams?: Record<string, string | number | undefined>;
}): Promise<SearchResult> {
  const response = await api.get(endpoint, {
    params: {
      search,
      per_page: perPage,
      ...extraParams
    }
  });
  return searchResultSchema.parse(response.data);
}

/**
 * React Query wrapper around `searchRepositories` shared by the
 * migration wizard and the env builder wizard. Both features have the
 * same data-layer needs:
 *
 * - debounced server-side search (parent supplies the debounced value)
 * - `keepPreviousData` so the dropdown doesn't flicker between keystrokes
 * - 30s `staleTime` (responses change slowly)
 *
 * UI shells differ (multi-select vs single-select, per-row content,
 * footer affordances) so each feature still owns its own dropdown.
 */
export function useRepositorySearch(t0) {
  const $ = _c(9);
  const {
    endpoint,
    search,
    perPage: t1,
    enabled: t2,
    queryKey,
    extraParams,
    options
  } = t0;
  const perPage = t1 === undefined ? 100 : t1;
  const enabled = t2 === undefined ? true : t2;
  const api = useApi();
  let t3;
  if ($[0] !== api || $[1] !== endpoint || $[2] !== extraParams || $[3] !== perPage || $[4] !== search) {
    t3 = () => searchRepositories({
      api,
      endpoint,
      search,
      perPage,
      extraParams
    });
    $[0] = api;
    $[1] = endpoint;
    $[2] = extraParams;
    $[3] = perPage;
    $[4] = search;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== enabled || $[7] !== options) {
    t4 = {
      enabled,
      keepPreviousData: true,
      staleTime: 30000,
      ...options
    };
    $[6] = enabled;
    $[7] = options;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useQuery(queryKey, t3, t4);
}